export type SfxId =
  | 'hazard.warning'
  | 'punch'
  | 'shot.small'
  | 'shot.heavy'
  | 'shot.rifle'
  | 'shot.shotgun'
  | 'shot.sniper'
  | 'shot.rocket'
  | 'shot.launcher'
  | 'shot.snake'
  | 'shot.lava'
  | 'shot.beam'
  | 'shot.stream'
  | 'shot.laser'
  | 'shot.ice'
  | 'shot.glue'
  | 'shot.minigun'
  | 'shot.flame'
  | 'shot.bounce'
  | 'shot.god'
  | 'shot.disc'
  | 'shot.siphon'
  | 'melee.sword'
  | 'melee.spear'
  | 'melee.blink'
  | 'field.bubble'
  | 'field.hole'
  | 'hit'
  | 'block'
  | 'block.perfect'
  | 'clash'
  | 'explosion'
  | 'pickup'
  | 'jump'
  | 'death';

export type AudioVoice = { endsAt: number; stop: () => void };

function voice(source: AudioScheduledSourceNode, gain: GainNode, endsAt: number): AudioVoice {
  let stopped = false;
  const disconnect = () => {
    source.disconnect();
    gain.disconnect();
  };
  source.onended = disconnect;
  return {
    endsAt,
    stop() {
      if (stopped) return;
      stopped = true;
      source.stop();
      disconnect();
    },
  };
}

export function playTone(
  ctx: AudioContext,
  type: OscillatorType,
  freq: number,
  dur: number,
  gain: number,
  dest: AudioNode,
  when = ctx.currentTime,
): AudioVoice {
  const osc = ctx.createOscillator();
  const g = ctx.createGain();
  osc.type = type;
  osc.frequency.value = freq;
  g.gain.setValueAtTime(0.0001, when);
  g.gain.linearRampToValueAtTime(Math.max(0.0001, gain), when + Math.min(0.003, dur * 0.1));
  g.gain.exponentialRampToValueAtTime(0.0001, when + dur);
  osc.connect(g);
  g.connect(dest);
  const handle = voice(osc, g, when + dur);
  osc.start(when);
  osc.stop(when + dur);
  return handle;
}

export function playNoise(
  ctx: AudioContext,
  dur: number,
  gain: number,
  dest: AudioNode,
  when = ctx.currentTime,
): AudioVoice {
  const buffer = ctx.createBuffer(1, ctx.sampleRate * dur, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
  const src = ctx.createBufferSource();
  src.buffer = buffer;
  const g = ctx.createGain();
  g.gain.setValueAtTime(Math.max(0.0001, gain), when);
  g.gain.exponentialRampToValueAtTime(0.0001, when + dur);
  src.connect(g);
  g.connect(dest);
  const handle = voice(src, g, when + dur);
  src.start(when);
  src.stop(when + dur);
  return handle;
}

export function playSfx(ctx: AudioContext, dest: AudioNode, id: SfxId): void {
  switch (id) {
    case 'hazard.warning':
      playTone(ctx, 'sine', 660, 0.08, 0.045, dest);
      playTone(ctx, 'sine', 880, 0.08, 0.045, dest, ctx.currentTime + 0.12);
      break;
    case 'punch':
      // a short whoosh: filtered-sounding noise with a low thump under it
      playNoise(ctx, 0.07, 0.05, dest);
      playTone(ctx, 'sine', 140, 0.06, 0.06, dest);
      break;
    case 'block':
      // a dull "tunk" off the guard
      playTone(ctx, 'triangle', 520, 0.05, 0.1, dest);
      playNoise(ctx, 0.03, 0.05, dest);
      break;
    case 'block.perfect':
      // a bright ping for the perfect window
      playTone(ctx, 'sine', 1240, 0.14, 0.09, dest);
      playTone(ctx, 'triangle', 830, 0.1, 0.06, dest);
      break;
    case 'clash':
      // two fists meeting: a clang with a metallic tail
      playTone(ctx, 'square', 310, 0.09, 0.1, dest);
      playTone(ctx, 'sawtooth', 620, 0.16, 0.06, dest);
      playNoise(ctx, 0.08, 0.08, dest);
      break;
    case 'shot.small':
    case 'shot.rifle':
    case 'shot.laser':
      playTone(ctx, 'square', 420, 0.05, 0.08, dest);
      playNoise(ctx, 0.04, 0.05, dest);
      break;
    case 'shot.heavy':
    case 'shot.sniper':
    case 'shot.god':
      playTone(ctx, 'sawtooth', 180, 0.12, 0.14, dest);
      playNoise(ctx, 0.08, 0.08, dest);
      break;
    case 'shot.shotgun':
    case 'explosion':
      playNoise(ctx, 0.25, 0.2, dest);
      playTone(ctx, 'triangle', 70, 0.2, 0.16, dest);
      break;
    case 'shot.rocket':
    case 'shot.launcher':
      playTone(ctx, 'sawtooth', 110, 0.18, 0.12, dest);
      break;
    case 'shot.disc':
      // A light metallic pulse with a brief overtone, clear above the arena without a gunshot crack.
      playTone(ctx, 'sine', 620, 0.09, 0.04, dest);
      playTone(ctx, 'triangle', 930, 0.055, 0.025, dest, ctx.currentTime + 0.018);
      break;
    case 'shot.siphon':
      // A softer descending pair makes the short drain sound distinct from the disc and firearms.
      playTone(ctx, 'sine', 390, 0.07, 0.035, dest);
      playTone(ctx, 'sine', 260, 0.14, 0.04, dest, ctx.currentTime + 0.035);
      break;
    case 'pickup':
      playTone(ctx, 'sine', 520, 0.1, 0.08, dest);
      break;
    case 'hit':
    case 'death':
      playNoise(ctx, 0.12, 0.1, dest);
      break;
    default:
      playTone(ctx, 'square', 240, 0.06, 0.07, dest);
  }
}
